import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BoldOutlined,
  CloseOutlined,
  CodeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  ReloadOutlined,
  SaveOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '@api/server';
import { applyMarkdownFormat, getTextAreaElement, insertMarkdownImage } from '../markdown/markdown-format';
import MarkdownView from '../markdown/markdown-view';
import {
  createArticle,
  getArticle,
  getArticleChunks,
  getArticles,
  recreateArticleChunks,
  updateArticle,
  uploadArticleMedia,
} from './articles';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const MARKDOWN_TOOLBAR_ACTIONS = [
  { key: 'bold', title: 'Жирный', icon: <BoldOutlined /> },
  { key: 'italic', title: 'Курсив', icon: <ItalicOutlined /> },
  { key: 'strike', title: 'Зачёркнутый', icon: <StrikethroughOutlined /> },
  { key: 'code', title: 'Код', icon: <CodeOutlined /> },
  { type: 'divider' },
  { key: 'h1', title: 'Заголовок 1', label: 'H1' },
  { key: 'h2', title: 'Заголовок 2', label: 'H2' },
  { key: 'h3', title: 'Заголовок 3', label: 'H3' },
  { type: 'divider' },
  { key: 'ul', title: 'Маркированный список', icon: <UnorderedListOutlined /> },
  { key: 'ol', title: 'Нумерованный список', icon: <OrderedListOutlined /> },
  { key: 'quote', title: 'Цитата', label: '>' },
  { key: 'link', title: 'Ссылка', icon: <LinkOutlined /> },
  { key: 'codeblock', title: 'Блок кода', label: '```' },
];

const ARTICLE_DRAWER_WIDTH_KEY = 'botva.articles.drawerWidth';
const ARTICLE_CHUNK_DRAWER_WIDTH_KEY = 'botva.articles.chunkDrawerWidth';
const MIN_ARTICLE_DRAWER_WIDTH = 560;
const DEFAULT_ARTICLE_DRAWER_WIDTH = 720;
const DEFAULT_CHUNK_DRAWER_WIDTH = 720;
const DRAWER_VIEWPORT_GAP = 48;
const CHUNK_PREVIEW_CHARS = 160;

const getInitialDrawerWidth = (storageKey, defaultWidth, minWidth = MIN_ARTICLE_DRAWER_WIDTH) => {
  const savedWidth = Number(localStorage.getItem(storageKey));
  if (Number.isFinite(savedWidth) && savedWidth >= minWidth) {
    return savedWidth;
  }

  return defaultWidth;
};

const clampDrawerWidth = (width, minWidth = MIN_ARTICLE_DRAWER_WIDTH) => {
  const maxWidth = Math.max(minWidth, window.innerWidth - DRAWER_VIEWPORT_GAP);
  return Math.min(Math.max(width, minWidth), maxWidth);
};

const formatChunkMetadata = (metadata) => {
  if (metadata == null || metadata === '') {
    return '';
  }

  if (typeof metadata === 'object') {
    return JSON.stringify(metadata, null, 2);
  }

  const raw = String(metadata);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const IMAGE_MAX_BYTES = 8 * 1024 * 1024;
const IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    const comma = result.indexOf(',');
    resolve(comma >= 0 ? result.slice(comma + 1) : result);
  };
  reader.onerror = () => reject(new Error('Не удалось прочитать файл.'));
  reader.readAsDataURL(file);
});

const MarkdownToolbar = ({ onAction, onPickImage, imageDisabled, imageTitle, imageLoading, disabled }) => (
  <div className="markdown-editor-format-toolbar">
    {MARKDOWN_TOOLBAR_ACTIONS.map((action, index) => {
      if (action.type === 'divider') {
        return <Divider key={`divider-${index}`} type="vertical" />;
      }

      return (
        <Tooltip key={action.key} title={action.title}>
          <Button
            type="text"
            size="small"
            disabled={disabled}
            aria-label={action.title}
            icon={action.icon}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onAction(action.key)}
          >
            {action.label}
          </Button>
        </Tooltip>
      );
    })}
    <Divider type="vertical" />
    <Tooltip title={imageTitle}>
      <Button
        type="text"
        size="small"
        disabled={disabled || imageDisabled}
        loading={imageLoading}
        aria-label="Картинка"
        icon={<PictureOutlined />}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onPickImage}
      />
    </Tooltip>
  </div>
);

const MarkdownField = ({ name, placeholder, minRows = 8, rules, mode, spaceId, articleId, onError }) => {
  const form = Form.useFormInstance();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const isPreview = mode === 'preview';
  const canUploadImage = Boolean(spaceId && articleId);

  const handleFormat = useCallback((format) => {
    const textarea = getTextAreaElement(textareaRef);
    const value = form.getFieldValue(name) || '';
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const next = applyMarkdownFormat(value, start, end, format);

    form.setFieldsValue({ [name]: next.value });

    requestAnimationFrame(() => {
      const nextTextarea = getTextAreaElement(textareaRef);
      if (!nextTextarea) {
        return;
      }

      nextTextarea.focus();
      nextTextarea.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  }, [form, name]);

  const applyEditorValue = useCallback((next) => {
    form.setFieldsValue({ [name]: next.value });
    requestAnimationFrame(() => {
      const nextTextarea = getTextAreaElement(textareaRef);
      if (!nextTextarea) {
        return;
      }

      nextTextarea.focus();
      nextTextarea.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  }, [form, name]);

  const handleImageFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!canUploadImage) {
      onError?.('Сначала создайте статью.');
      return;
    }
    if (!IMAGE_CONTENT_TYPES.includes(file.type)) {
      onError?.('Можно загрузить png, jpeg, gif или webp.');
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      onError?.('Картинка больше 8 МБ.');
      return;
    }

    setIsUploadingImage(true);
    onError?.('');
    try {
      const dataBase64 = await fileToBase64(file);
      const uploaded = await uploadArticleMedia({
        spaceid: spaceId,
        articleid: articleId,
        filename: file.name,
        contenttype: file.type === 'image/jpg' ? 'image/jpeg' : file.type,
        dataBase64,
      });
      const textarea = getTextAreaElement(textareaRef);
      const value = form.getFieldValue(name) || '';
      const start = textarea?.selectionStart ?? value.length;
      const end = textarea?.selectionEnd ?? value.length;
      const alt = file.name.replace(/\.[^.]+$/, '') || 'картинка';
      applyEditorValue(insertMarkdownImage(value, start, end, alt, uploaded.url));
    } catch (requestError) {
      onError?.(getApiErrorMessage(requestError));
    } finally {
      setIsUploadingImage(false);
    }
  }, [applyEditorValue, articleId, canUploadImage, form, name, onError, spaceId]);

  return (
    <Form.Item
      noStyle
      shouldUpdate={(previousValues, nextValues) => previousValues[name] !== nextValues[name]}
    >
      {({ getFieldValue }) => {
        const value = getFieldValue(name) || '';

        return (
          <div className="markdown-editor article-markdown-editor">
            {!isPreview && (
              <>
                <MarkdownToolbar
                  onAction={handleFormat}
                  onPickImage={() => fileInputRef.current?.click()}
                  imageDisabled={!canUploadImage}
                  imageLoading={isUploadingImage}
                  imageTitle={canUploadImage ? 'Картинка' : 'Сначала создайте статью'}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={IMAGE_CONTENT_TYPES.join(',')}
                  hidden
                  onChange={handleImageFile}
                />
              </>
            )}

            <div hidden={isPreview}>
              <Form.Item name={name} rules={rules} noStyle>
                <TextArea
                  ref={textareaRef}
                  className="markdown-editor-textarea"
                  placeholder={placeholder}
                  autoSize={{ minRows }}
                />
              </Form.Item>
            </div>

            {isPreview && (
              value ? (
                <MarkdownView>{value}</MarkdownView>
              ) : (
                <Text type="secondary">Markdown-текст пока пуст</Text>
              )
            )}
          </div>
        );
      }}
    </Form.Item>
  );
};

const Article = () => {
  const [metaForm] = Form.useForm();
  const [bodyForm] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [isSavingBody, setIsSavingBody] = useState(false);
  const [bodyMode, setBodyMode] = useState('markdown');
  const [spaceArticles, setSpaceArticles] = useState([]);
  const [spaceId, setSpaceId] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(() => (
    getInitialDrawerWidth(ARTICLE_DRAWER_WIDTH_KEY, DEFAULT_ARTICLE_DRAWER_WIDTH)
  ));
  const [activeTab, setActiveTab] = useState('main');
  const [chunks, setChunks] = useState([]);
  const [isChunksLoading, setIsChunksLoading] = useState(false);
  const [isRecreatingChunks, setIsRecreatingChunks] = useState(false);
  const [chunksError, setChunksError] = useState('');
  const [selectedChunk, setSelectedChunk] = useState(null);
  const [chunkDrawerWidth, setChunkDrawerWidth] = useState(() => (
    getInitialDrawerWidth(ARTICLE_CHUNK_DRAWER_WIDTH_KEY, DEFAULT_CHUNK_DRAWER_WIDTH, 480)
  ));

  const articleId = useMemo(() => {
    if (!id) {
      return null;
    }

    const nextId = Number(id);
    return Number.isInteger(nextId) && nextId > 0 ? nextId : null;
  }, [id]);
  const isNewArticle = !id;
  const title = isNewArticle ? 'Новая статья' : `Статья #${articleId || ''}`;
  const closeDrawer = useCallback(() => {
    navigate('/articles');
  }, [navigate]);

  const parentOptions = useMemo(() => {
    const blocked = new Set();
    if (articleId) {
      blocked.add(articleId);
      const collectDescendants = (parentId) => {
        spaceArticles.forEach((article) => {
          if (article.parentid === parentId && !blocked.has(article.id)) {
            blocked.add(article.id);
            collectDescendants(article.id);
          }
        });
      };
      collectDescendants(articleId);
    }

    return spaceArticles
      .filter((article) => !blocked.has(article.id))
      .map((article) => ({
        value: article.id,
        label: article.name,
      }));
  }, [articleId, spaceArticles]);

  const loadChunks = useCallback(async (targetArticleId) => {
    if (!targetArticleId) {
      setChunks([]);
      return;
    }

    setChunksError('');
    setIsChunksLoading(true);
    try {
      const result = await getArticleChunks(targetArticleId);
      setChunks(Array.isArray(result) ? result : []);
    } catch (requestError) {
      setChunks([]);
      setChunksError(getApiErrorMessage(requestError));
    } finally {
      setIsChunksLoading(false);
    }
  }, []);

  const loadArticle = useCallback(async () => {
    const querySpaceId = Number(searchParams.get('spaceid'));

    if (isNewArticle) {
      if (!Number.isInteger(querySpaceId) || querySpaceId <= 0) {
        setError('Выберите пространство перед созданием статьи.');
        return;
      }

      setSpaceId(querySpaceId);
      setChunks([]);
      setActiveTab('main');
      setBodyMode('markdown');
      metaForm.setFieldsValue({
        name: '',
        alias: '',
        description: '',
        parentid: null,
      });
      bodyForm.setFieldsValue({ body: '' });

      setIsLoading(true);
      try {
        const articles = await getArticles(querySpaceId);
        setSpaceArticles(articles || []);
      } catch (requestError) {
        setError(getApiErrorMessage(requestError));
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!articleId) {
      setError('Некорректный идентификатор статьи.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const article = await getArticle(articleId);
      setSpaceId(article.spaceid);
      metaForm.setFieldsValue({
        name: article.name,
        alias: article.alias || '',
        description: article.description || '',
        parentid: article.parentid || null,
      });
      bodyForm.setFieldsValue({
        body: article.body || '',
      });
      setActiveTab('body');
      setBodyMode('markdown');

      const articles = await getArticles(article.spaceid);
      setSpaceArticles(articles || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [articleId, bodyForm, isNewArticle, metaForm, searchParams]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  useEffect(() => {
    if (activeTab === 'chunks' && articleId) {
      loadChunks(articleId);
    }
  }, [activeTab, articleId, loadChunks]);

  const handleSaveMeta = async (values) => {
    if (!spaceId) {
      setError('Пространство не выбрано.');
      return;
    }

    setError('');
    setIsSavingMeta(true);
    try {
      const payload = {
        name: values.name?.trim(),
        alias: values.alias?.trim() || null,
        description: values.description?.trim() || null,
        parentid: values.parentid || null,
        spaceid: spaceId,
      };

      if (isNewArticle) {
        const created = await createArticle({
          ...payload,
          body: bodyForm.getFieldValue('body') || '',
        });
        navigate(`/articles/${created.id}`, { replace: true });
        return;
      }

      await updateArticle({
        id: articleId,
        mode: 'meta',
        ...payload,
      });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSavingMeta(false);
    }
  };

  const handleSaveBody = async (values) => {
    if (isNewArticle) {
      setError('Сначала сохраните поля статьи.');
      return;
    }

    setError('');
    setIsSavingBody(true);
    try {
      const updated = await updateArticle({
        id: articleId,
        mode: 'body',
        body: values.body || '',
      });
      bodyForm.setFieldsValue({ body: updated.body || '' });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSavingBody(false);
    }
  };

  const handleRecreateChunks = async () => {
    if (!articleId) {
      return;
    }

    setChunksError('');
    setIsRecreatingChunks(true);
    try {
      await recreateArticleChunks(articleId);
      await loadChunks(articleId);
    } catch (requestError) {
      setChunksError(getApiErrorMessage(requestError));
    } finally {
      setIsRecreatingChunks(false);
    }
  };

  const handleResizeStart = useCallback((event) => {
    event.preventDefault();

    const handleMouseMove = (moveEvent) => {
      const nextWidth = clampDrawerWidth(window.innerWidth - moveEvent.clientX);
      setDrawerWidth(nextWidth);
      localStorage.setItem(ARTICLE_DRAWER_WIDTH_KEY, String(nextWidth));
    };

    const handleMouseUp = () => {
      document.body.classList.remove('drawer-resizing');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.classList.add('drawer-resizing');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleChunkResizeStart = useCallback((event) => {
    event.preventDefault();

    const handleMouseMove = (moveEvent) => {
      const nextWidth = clampDrawerWidth(window.innerWidth - moveEvent.clientX, 480);
      setChunkDrawerWidth(nextWidth);
      localStorage.setItem(ARTICLE_CHUNK_DRAWER_WIDTH_KEY, String(nextWidth));
    };

    const handleMouseUp = () => {
      document.body.classList.remove('drawer-resizing');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.classList.add('drawer-resizing');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  const selectedChunkMetadata = formatChunkMetadata(selectedChunk?.metadata);
  const selectedChunkTitle = selectedChunk
    ? [
      'Чанк',
      selectedChunk.idx != null ? `#${selectedChunk.idx}` : null,
      selectedChunk.id != null ? `id ${selectedChunk.id}` : null,
    ].filter(Boolean).join(' · ')
    : 'Чанк';

  const mainTab = (
    <Form
      form={metaForm}
      layout="vertical"
      onFinish={handleSaveMeta}
      requiredMark={false}
      initialValues={{
        name: '',
        alias: '',
        description: '',
        parentid: null,
      }}
    >
      <div className="form-row">
        <Form.Item
          label="Название"
          name="name"
          rules={[
            { required: true, message: 'Введите название статьи.' },
            { max: 255, message: 'Название должно быть не длиннее 255 символов.' },
          ]}
        >
          <Input placeholder="Название статьи" autoFocus={isNewArticle} />
        </Form.Item>

        <Form.Item
          label="Alias"
          name="alias"
          rules={[{ max: 255, message: 'Alias должен быть не длиннее 255 символов.' }]}
        >
          <Input placeholder="alias" />
        </Form.Item>
      </div>

      <Form.Item label="Описание" name="description">
        <TextArea placeholder="Краткое описание" autoSize={{ minRows: 2 }} />
      </Form.Item>

      <Form.Item label="Родитель" name="parentid">
        <Select
          allowClear
          placeholder="Без родителя"
          options={parentOptions}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={isSavingMeta}
          onClick={() => metaForm.submit()}
        >
          {isNewArticle ? 'Создать' : 'Сохранить поля'}
        </Button>
      </Form.Item>
    </Form>
  );

  const bodyTab = (
    <div className="article-body-tab">
      <div className="article-body-toolbar">
        <Segmented
          size="small"
          value={bodyMode}
          onChange={setBodyMode}
          options={[
            { label: 'Markdown', value: 'markdown' },
            { label: 'Просмотр', value: 'preview' },
          ]}
        />
      </div>
      <Form
        form={bodyForm}
        layout="vertical"
        onFinish={handleSaveBody}
        requiredMark={false}
        initialValues={{ body: '' }}
      >
        <MarkdownField
          name="body"
          placeholder={'# Заголовок\n\nТекст статьи в Markdown…'}
          mode={bodyMode}
          spaceId={spaceId}
          articleId={articleId}
          onError={setError}
        />

        <Form.Item className="article-body-save">
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isSavingBody}
              disabled={isNewArticle}
              onClick={() => bodyForm.submit()}
            >
              Сохранить содержимое
            </Button>
            {isNewArticle && (
              <Text type="secondary">Сначала создайте статью</Text>
            )}
          </Space>
        </Form.Item>
      </Form>
    </div>
  );

  const chunksTab = (
    <div className="article-chunks-tab">
      <div className="article-chunks-toolbar">
        <Text type="secondary">
          {isChunksLoading ? 'Загрузка…' : `Чанков: ${chunks.length}`}
        </Text>
        <Popconfirm
          title="Пересоздать чанки?"
          description="Старые чанки статьи будут удалены и созданы заново."
          okText="Пересоздать"
          cancelText="Отмена"
          okButtonProps={{ loading: isRecreatingChunks }}
          onConfirm={handleRecreateChunks}
          disabled={isNewArticle}
        >
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={isRecreatingChunks}
            disabled={isNewArticle}
          >
            Пересоздать
          </Button>
        </Popconfirm>
      </div>

      {chunksError && (
        <Alert className="user-alert" type="error" message={chunksError} showIcon />
      )}

      <Spin spinning={isChunksLoading || isRecreatingChunks}>
        {isNewArticle ? (
          <Empty description="Сначала создайте статью" />
        ) : chunks.length === 0 ? (
          <Empty description="Чанков пока нет" />
        ) : (
          <List
            className="article-chunks-list"
            dataSource={chunks}
            renderItem={(chunk) => {
              const text = chunk.text || '';
              const charCount = text.length;
              const preview = text.length > CHUNK_PREVIEW_CHARS
                ? `${text.slice(0, CHUNK_PREVIEW_CHARS)}…`
                : text;

              return (
                <List.Item
                  className="article-chunk-list-item"
                  onClick={() => setSelectedChunk(chunk)}
                >
                  <div className="article-chunk-list-item-content">
                    <div className="article-chunk-list-item-meta">
                      <Tag>#{chunk.idx ?? chunk.id}</Tag>
                      <Text type="secondary">{charCount} симв.</Text>
                    </div>
                    <Paragraph
                      className="article-chunk-list-item-preview"
                      ellipsis={{ rows: 2 }}
                    >
                      {preview || '—'}
                    </Paragraph>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Spin>
    </div>
  );

  return (
    <>
      <Drawer
        className="user-drawer user-form-drawer agent-resizable-drawer"
        title={title}
        placement="right"
        width={drawerWidth}
        open
        closable={false}
        onClose={closeDrawer}
        destroyOnHidden
        extra={(
          <Button
            type="text"
            icon={<CloseOutlined />}
            aria-label="Закрыть"
            onClick={closeDrawer}
          />
        )}
      >
        <div
          className="agent-drawer-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину окна статьи"
          onMouseDown={handleResizeStart}
        />

        {error && (
          <Alert className="user-alert" type="error" message={error} showIcon />
        )}

        <Spin spinning={isLoading}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'main',
                label: 'Основная',
                children: mainTab,
              },
              {
                key: 'body',
                label: 'Содержимое',
                forceRender: true,
                children: bodyTab,
              },
              {
                key: 'chunks',
                label: 'Чанки',
                disabled: isNewArticle,
                children: chunksTab,
              },
            ]}
          />
        </Spin>
      </Drawer>

      <Drawer
        className="user-drawer user-form-drawer agent-resizable-drawer"
        title={selectedChunkTitle}
        placement="right"
        width={chunkDrawerWidth}
        open={Boolean(selectedChunk)}
        onClose={() => setSelectedChunk(null)}
        destroyOnHidden
        footer={(
          <div className="user-drawer-footer">
            <Button onClick={() => setSelectedChunk(null)}>Закрыть</Button>
          </div>
        )}
      >
        <div
          className="agent-drawer-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину окна чанка"
          onMouseDown={handleChunkResizeStart}
        />

        <div className="space-chunk-section">
          <Text strong>Metadata</Text>
          {selectedChunkMetadata ? (
            <pre className="space-chunk-metadata">{selectedChunkMetadata}</pre>
          ) : (
            <Text type="secondary">Metadata пусто</Text>
          )}
        </div>

        <div className="space-chunk-section">
          <Text strong>Текст</Text>
          {selectedChunk?.text ? (
            <MarkdownView className="markdown-preview space-chunk-markdown">
              {selectedChunk.text}
            </MarkdownView>
          ) : (
            <Text type="secondary">Текст чанка пуст</Text>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default Article;
